import { useEffect, useState } from "react";
import { Cpu, MemoryStick, Network, HardDrive } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  CHART_GRID_STROKE,
  CHART_GRID_STROKE_OPACITY,
  CHART_TICK_FILL,
  chartTooltipContentStyle,
  formatMetricsTimestamp,
} from "@/lib/chartTheme";
import { formatPercent, percentAxisMax } from "@/lib/percentAxis";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  diskRateScale,
  peakDiskRate,
  useDiskRateUnit,
} from "@/hooks/useDiskRateUnit";
import type { MetricDataPoint, ResourceMetricsSeries } from "@/types/admin";

interface ResourceMetricsChartProps {
  metrics?: ResourceMetricsSeries;
  isLoading: boolean;
  isFetching: boolean;
  /** True while a newly picked range loads over the previous range's series. */
  isSwitchingRange: boolean;
  error: unknown;
  /** e.g. "1H" / "24H" / "7D" / "30D" / "1Y" — drives the X-axis timestamp granularity. */
  rangeLabel: string;
  /** Asks for the series again. Without it a failed read had no way back short of a reload. */
  onRetry?: () => void;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Failed to load metrics.";
}

const axisTick = { fill: CHART_TICK_FILL, fontSize: 11 };

interface MetricCardProps {
  title: string;
  /** Shown after the title in parentheses, e.g. "%", "Mbps", "KB/s". */
  unit: string;
  icon: React.ComponentType<{ className?: string }>;
  isInitialLoading: boolean;
  showFetching: boolean;
  children: React.ReactElement;
}

/** One chart card, with the client's ResourceMetricsTab padding, loading text and "Updating..." overlay. */
function MetricCard({
  title,
  unit,
  icon: Icon,
  isInitialLoading,
  showFetching,
  children,
}: MetricCardProps) {
  return (
    <Card className="relative border-border/80 shadow-none">
      <CardHeader className="p-4 pb-3 sm:p-6 sm:pb-6">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-4 w-4" />
          {title}{" "}
          <span className="font-normal text-muted-foreground">({unit})</span>
          {showFetching && (
            <span className="ml-auto text-xs text-muted-foreground">
              Updating...
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
        {isInitialLoading ? (
          <div className="flex h-[250px] w-full items-center justify-center text-muted-foreground">
            Loading...
          </div>
        ) : (
          <div className="relative">
            {showFetching && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/50 backdrop-blur-sm transition-opacity duration-200">
                <div className="text-sm text-muted-foreground">Updating...</div>
              </div>
            )}
            <ResponsiveContainer width="100%" height={250}>
              {children}
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Resource Metrics tab — the client app's ResourceMetricsTab charts, drawn from the same backend
 * series (CPU %, memory %, network inbound/outbound Mbps, disk read/write MB/s).
 *
 * The one addition is "CPU Usage, raw", after the client's four charts: the host reading before the
 * VmCpuLimitRatio rescale. Only CPU is rescaled; every other chart is the measured figure.
 */
export function ResourceMetricsChart({
  metrics,
  isLoading,
  isFetching,
  isSwitchingRange,
  error,
  rangeLabel,
  onRetry,
}: ResourceMetricsChartProps) {
  // Same as the client: the overlay marks a range switch only. A background poll redraws in place —
  // every provider call runs past the 500ms debounce, so keying this on isFetching blurred the
  // charts once a minute.
  const [showFetching, setShowFetching] = useState(false);
  // KB/s or MB/s for the disk chart (useDiskRateUnit), same rule as the client. Called up here, before
  // the early return below. Placeholder data from the previous range never moves it.
  const diskUnit = useDiskRateUnit(
    peakDiskRate(metrics?.disk.read ?? [], metrics?.disk.write ?? []),
    !!metrics && !isSwitchingRange,
  );
  const diskScale = diskRateScale(diskUnit);

  useEffect(() => {
    if (isSwitchingRange) {
      const timer = setTimeout(() => setShowFetching(true), 500);
      return () => clearTimeout(timer);
    }
    setShowFetching(false);
  }, [isSwitchingRange]);

  const retryButton = onRetry && (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={onRetry}
      disabled={isFetching}
    >
      {isFetching && <Spinner className="mr-2" />}
      Retry
    </Button>
  );

  // Only when there is nothing to draw. The query polls once a minute and keeps its last series when
  // a poll fails, so `if (error)` let one missed tick replace every chart the operator was reading.
  if (error && !metrics) {
    return (
      <div className="space-y-3 rounded-xl border border-destructive/50 bg-destructive/10 p-6">
        <p className="text-sm text-destructive">{errorMessage(error)}</p>
        {retryButton}
      </div>
    );
  }

  const isInitialLoading = isLoading && !metrics;
  const formatTimestamp = (timestamp: string) =>
    formatMetricsTimestamp(timestamp, rangeLabel);

  const toChartData = (series?: MetricDataPoint[]) =>
    series?.map((point) => ({
      time: formatTimestamp(point.timestamp),
      value: point.value,
    })) ?? [];

  const cpuData = toChartData(metrics?.cores);
  const hostCpuData = toChartData(metrics?.hostCores);
  const memoryData = toChartData(metrics?.memory);

  const networkData =
    metrics?.network.inbound.map((point, index) => ({
      time: formatTimestamp(point.timestamp),
      inbound: point.value,
      outbound: metrics.network.outbound[index]?.value || 0,
    })) ?? [];

  const diskData =
    metrics?.disk.read.map((point, index) => ({
      time: formatTimestamp(point.timestamp),
      read: point.value * diskScale,
      write: (metrics.disk.write[index]?.value || 0) * diskScale,
    })) ?? [];

  const cardState = { isInitialLoading, showFetching };

  const percentChart = (
    data: { time: string; value: number }[],
    gradientId: string,
    stroke: string,
    tooltipLabel: string,
    axisMax: number | ((dataMax: number) => number),
  ) => (
    <AreaChart data={data}>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor={stroke} stopOpacity={0.8} />
          <stop offset="95%" stopColor={stroke} stopOpacity={0} />
        </linearGradient>
      </defs>
      <CartesianGrid
        strokeDasharray="3 3"
        stroke={CHART_GRID_STROKE}
        strokeOpacity={CHART_GRID_STROKE_OPACITY}
      />
      <XAxis
        dataKey="time"
        tick={axisTick}
        stroke={CHART_GRID_STROKE}
        strokeOpacity={CHART_GRID_STROKE_OPACITY}
      />
      <YAxis
        tick={axisTick}
        stroke={CHART_GRID_STROKE}
        strokeOpacity={CHART_GRID_STROKE_OPACITY}
        domain={[0, axisMax]}
        tickFormatter={(v: number) => `${v}%`}
      />
      <Tooltip
        contentStyle={chartTooltipContentStyle}
        formatter={(value) => [formatPercent(value), tooltipLabel]}
      />
      {/* No animation: each poll slides the window one sample, and the default 1.5s tween moved
          every point to its neighbour's value, so the whole line rippled once a minute. */}
      <Area
        isAnimationActive={false}
        type="monotone"
        dataKey="value"
        stroke={stroke}
        fillOpacity={1}
        fill={`url(#${gradientId})`}
      />
    </AreaChart>
  );

  return (
    <div className="space-y-4">
      {Boolean(error) && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-warn/25 bg-warn-soft px-3 py-2 text-sm text-warn">
          <span>
            Could not refresh — showing the last loaded samples.{" "}
            {errorMessage(error)}
          </span>
          {retryButton}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <MetricCard title="CPU Usage" unit="%" icon={Cpu} {...cardState}>
          {/* Scales to the reading, so a VM idling under 1% still draws a visible line. */}
          {percentChart(
            cpuData,
            "cpuGradient",
            "var(--info)",
            "CPU",
            percentAxisMax,
          )}
        </MetricCard>

        <MetricCard title="Memory Usage" unit="%" icon={MemoryStick} {...cardState}>
          {percentChart(
            memoryData,
            "memoryGradient",
            "var(--brand)",
            "RAM",
            100,
          )}
        </MetricCard>

        <MetricCard title="Network Traffic" unit="Mbps" icon={Network} {...cardState}>
          <LineChart data={networkData}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={CHART_GRID_STROKE}
              strokeOpacity={CHART_GRID_STROKE_OPACITY}
            />
            <XAxis
              dataKey="time"
              tick={axisTick}
              stroke={CHART_GRID_STROKE}
              strokeOpacity={CHART_GRID_STROKE_OPACITY}
            />
            <YAxis
              tick={axisTick}
              stroke={CHART_GRID_STROKE}
              strokeOpacity={CHART_GRID_STROKE_OPACITY}
            />
            <Tooltip
              itemSorter="dataKey"
              contentStyle={chartTooltipContentStyle}
              formatter={(value, name) => [`${Number(value).toFixed(2)} Mbps`, name]}
            />
            <Legend itemSorter="dataKey" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
            <Line
              isAnimationActive={false}
              type="monotone"
              dataKey="inbound"
              stroke="var(--ok)"
              strokeWidth={2}
              dot={false}
              name="In"
            />
            <Line
              isAnimationActive={false}
              type="monotone"
              dataKey="outbound"
              stroke="var(--warn)"
              strokeWidth={2}
              dot={false}
              name="Out"
            />
          </LineChart>
        </MetricCard>

        <MetricCard title="Disk I/O" unit={diskUnit} icon={HardDrive} {...cardState}>
          <LineChart data={diskData}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={CHART_GRID_STROKE}
              strokeOpacity={CHART_GRID_STROKE_OPACITY}
            />
            <XAxis
              dataKey="time"
              tick={axisTick}
              stroke={CHART_GRID_STROKE}
              strokeOpacity={CHART_GRID_STROKE_OPACITY}
            />
            <YAxis
              tick={axisTick}
              stroke={CHART_GRID_STROKE}
              strokeOpacity={CHART_GRID_STROKE_OPACITY}
            />
            <Tooltip
              itemSorter="dataKey"
              contentStyle={chartTooltipContentStyle}
              formatter={(value, name) => [`${Number(value).toFixed(2)} ${diskUnit}`, name]}
            />
            <Legend itemSorter="dataKey" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
            <Line
              isAnimationActive={false}
              type="monotone"
              dataKey="read"
              stroke="var(--brand-hover)"
              strokeWidth={2}
              dot={false}
              name="Read"
            />
            <Line
              isAnimationActive={false}
              type="monotone"
              dataKey="write"
              stroke="var(--destructive)"
              strokeWidth={2}
              dot={false}
              name="Write"
            />
          </LineChart>
        </MetricCard>

        {/* Admin only, after the client's four charts: the CPU series before the VmCpuLimitRatio
            rescale. */}
        <MetricCard title="CPU Usage, raw" unit="%" icon={Cpu} {...cardState}>
          {percentChart(
            hostCpuData,
            "hostCpuGradient",
            "var(--info)",
            "CPU (raw)",
            percentAxisMax,
          )}
        </MetricCard>
      </div>
    </div>
  );
}
